import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  Trash2,
  Plus,
  Loader2,
  Key,
  Copy,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import {
  MCPKey,
  MCPKeyCreated,
  MCPKeyCreate,
  listMCPKeys,
  createMCPKey,
  revokeMCPKey,
} from '@/services/mcpKeysApi';

interface MCPKeysTabProps {
  className?: string;
}

export function MCPKeysTab({ className }: MCPKeysTabProps) {
  const { toast } = useToast();
  const [keys, setKeys] = useState<MCPKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCreatedDialog, setShowCreatedDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newKey, setNewKey] = useState<MCPKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  // Form state
  const [formData, setFormData] = useState<MCPKeyCreate>({
    name: '',
    description: '',
  });

  const loadKeys = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await listMCPKeys();
      setKeys(data);
    } catch (error) {
      console.error('Failed to load MCP keys:', error);
      toast({
        title: 'Error',
        description: 'Failed to load MCP API keys',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async () => {
    if (!formData.name?.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Name is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await createMCPKey(formData);
      setNewKey(created);
      setShowCreateDialog(false);
      setShowCreatedDialog(true);
      setFormData({ name: '', description: '' });
      await loadKeys();
      toast({
        title: 'Success',
        description: 'MCP API key created',
      });
    } catch (error) {
      console.error('Failed to create key:', error);
      toast({
        title: 'Error',
        description: 'Failed to create MCP API key',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevoke = async (keyId: string, keyName: string) => {
    if (!confirm(`Are you sure you want to revoke the key "${keyName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await revokeMCPKey(keyId);
      await loadKeys();
      toast({
        title: 'Success',
        description: 'MCP API key revoked',
      });
    } catch (error) {
      console.error('Failed to revoke key:', error);
      toast({
        title: 'Error',
        description: 'Failed to revoke MCP API key',
        variant: 'destructive',
      });
    }
  };

  const handleCopy = async () => {
    if (newKey?.api_key) {
      await navigator.clipboard.writeText(newKey.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Copied',
        description: 'API key copied to clipboard',
      });
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get the MCP server URL for display
  const mcpServerUrl = `${window.location.origin}/api/mcp`;

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                MCP API Keys
              </CardTitle>
              <CardDescription>
                Create API keys to access assistant tools via MCP protocol from external clients like Claude Code.
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* MCP Server Info */}
          <Alert className="mb-4">
            <ExternalLink className="h-4 w-4" />
            <AlertTitle>MCP Server URL</AlertTitle>
            <AlertDescription className="mt-2">
              <code className="bg-muted px-2 py-1 rounded text-sm">{mcpServerUrl}</code>
              <p className="mt-2 text-sm text-muted-foreground">
                Use this URL with your MCP client. Authenticate using the <code>X-Api-Key</code> header.
              </p>
            </AlertDescription>
          </Alert>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No MCP API keys yet</p>
              <p className="text-sm mt-1">Create a key to enable external MCP access</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{key.name || 'Unnamed'}</div>
                        {key.description && (
                          <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                            {key.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {key.token_hint}
                      </code>
                    </TableCell>
                    <TableCell>
                      {key.is_active ? (
                        <Badge variant="default" className="bg-green-500">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Revoked</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(key.last_used_at)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {key.usage_count.toLocaleString()} calls
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(key.created_at)}
                    </TableCell>
                    <TableCell>
                      {key.is_active && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRevoke(key.id, key.name || 'Unnamed')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create MCP API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for external MCP clients to access assistant tools.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Claude Code"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description for this key"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key Created Dialog */}
      <Dialog open={showCreatedDialog} onOpenChange={setShowCreatedDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              Copy this key now. You won't be able to see it again!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                This is the only time you'll see this API key. Copy it and store it securely.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label>Your API Key</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={newKey?.api_key || ''}
                  className="font-mono text-sm"
                />
                <Button variant="outline" onClick={handleCopy}>
                  {copied ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="bg-muted p-4 rounded-lg text-sm space-y-2">
              <p className="font-medium">Usage:</p>
              <p>Add this to your MCP client configuration:</p>
              <pre className="bg-background p-2 rounded text-xs overflow-x-auto">
{`{
  "mcpServers": {
    "hedgefund2": {
      "url": "${mcpServerUrl}",
      "headers": {
        "X-Api-Key": "${newKey?.api_key || 'your-api-key'}"
      }
    }
  }
}`}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowCreatedDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MCPKeysTab;
